import React, { useState, useEffect, useContext } from 'react';
import { FiCheck, FiLoader } from 'react-icons/fi';
import { HiSparkles } from 'react-icons/hi';
import { CreateSubscription, getPlans, getSubscriptionStatus, UpdateSubscription } from '../../../api/stripeApi';
import DeleteModal from '../../common/commonModal/DeleteModal';
import * as utilConstants from '../../../utils/constants/utilsConstants';
import ReactNotifications from '../../common/reactNotifications/ReactNotifications';
import { showApiErrors } from '../../common/reactNotifications/ReactNotifications';
import Context from '../../../context/Context';


const ACCENT_COLOR_TEXT = 'text-[#AEF468]';
const ACCENT_COLOR_BG = 'bg-[#AEF468]';

export default function PricingPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentUserPlan, setCurrentUserPlan] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [IsConfirmation, setIsConfirmation] = useState(false);
  const [confirmationTitle, setConfirmationTitle] = useState("");
  const [confirmationButtonText, setConfirmationButtonText] = useState("");
  const [pendingPlan, setPendingPlan] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { setPageTitle } = useContext(Context);

  useEffect(() => {
    fetchPlans();
    setPageTitle('AI Plans');
  }, [setPageTitle]);

  const fetchUserSubscription = (plans) => {
    getSubscriptionStatus({}, (data) => {
      if (data?.status) {
        setIsSubscribed(data?.has_active_subscription);
        if (!data.has_active_subscription) {
          const freePlan = plans.find((p) => p.is_free);
          if (freePlan) {
            setCurrentUserPlan(freePlan.id);
            setSelectedPlan(freePlan.id);
          }
          return;
        }

        setCurrentUserPlan(data.subscription.plan);
        setSelectedPlan(data.subscription.plan);
      }
    });
  };



  const fetchPlans = () => {
    try {
      setLoading(true);
      getPlans({}, (data) => {
        if (data.status && Array.isArray(data.plans)) {
          const mappedPlans = data.plans.map((plan) => ({
            ...plan,
            amountAnnual: plan.amountAnnual ?? plan.amount * 10,
            features: plan.is_free
              ? [
                "Access to core features",
                "Basic AI generation limits",
                "Email support"
              ]
              : [
                "Unlimited AI Generations",
                "Priority Support",
                "Analytics Dashboard",
                "API Access",
              ],
            is_popular: plan.name.toLowerCase().includes("premium"),
          }));

          setPlans(mappedPlans);
          fetchUserSubscription(mappedPlans);

          if (!currentUserPlan) {
            const defaultSelected = mappedPlans.find((p) => p.is_free);
            if (defaultSelected) setSelectedPlan(defaultSelected.id);
          }
        } else {
          setError("Failed to load plans.");
        }
      });
    } catch (err) {
      console.error(err);
      setError("Error fetching plans.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    setPendingPlan(plan);

    if (currentUserPlan === plan.id) {
      return;
    }

    if (plan.is_free) {
      setConfirmationTitle("Downgrade to Free Plan");
      setConfirmationButtonText("Confirm Downgrade");
    } else {
      setConfirmationTitle(`Activate ${plan.name}`);
      setConfirmationButtonText("Confirm Upgrade");
    }

    setIsConfirmation(true);
  };

  const handleConfirmation = () => {
    if (!pendingPlan) return;

    const payload = { plan_id: pendingPlan.id };

    if (isSubscribed) {
      UpdateSubscription(payload, (res) => {
        if (res?.status) {
          ReactNotifications(res?.message || res?.data?.message, utilConstants.sucessState200);
          fetchPlans();
        } else {
          showApiErrors(res);
        }
      });
    } else {
      CreateSubscription(payload, (res) => {
        if (res?.status && res?.url) {
          window.location.href = res.url;
        } else {
          showApiErrors(res);
        }
      });
    }

    setIsConfirmation(false);
  };




  const getPrice = (plan) => {
    if (plan.is_free) return "0";
    const price = plan.amount;
    return parseFloat(price).toFixed(0);
  };

  const getInterval = (plan) => {
    if (plan.is_free) return "";
    return isAnnual ? "/year" : `/${plan.interval}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <FiLoader className={`w-10 h-10 animate-spin ${ACCENT_COLOR_TEXT} mx-auto mb-4`} />
          <p className="text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 py-4 px-6">
      <div className="max-w-6xl mx-auto">

        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold">Choose Your Perfect Plan</h1>
          <p className="text-gray-600 mt-2">
            Flexible pricing designed to scale with your needs.
          </p>
        </div>

        <div className="flex justify-center mb-10">
          <div className="flex p-1 bg-gray-100 rounded-xl shadow-inner">
            <button
              onClick={() => setIsAnnual(false)}
              className={`py-2 px-6 rounded-lg font-medium 
                ${!isAnnual ? `${ACCENT_COLOR_BG} text-gray-900` : "text-gray-500"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`py-2 px-6 rounded-lg font-medium relative
                ${isAnnual ? `${ACCENT_COLOR_BG} text-gray-900` : "text-gray-500"}`}
            >
              Annual
              <span className="absolute -top-3 right-0 bg-black text-white text-xs px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {plans
            .filter((plan) => {
              if (plan.is_free) return true;
              if (!isAnnual && plan.interval === "month") return true;
              if (isAnnual && plan.interval === "year") return true;
              return false;
            })
            .map((plan) => (
              <div
                key={plan.id}
                className={`
          relative bg-white rounded-2xl p-8 shadow-xl border transition-all d-flex flex-col justify-between
          ${selectedPlan === plan.id ? `scale-[1.02] ring-2 ring-offset-2 ring-[#0A121A]` : ""}
        `}
              >
                {plan.is_popular && (
                  <div className={`absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 
            flex items-center ${ACCENT_COLOR_BG} text-gray-900 px-4 py-1 rounded-full shadow-md`}>
                    <HiSparkles className="w-4 h-4 mr-1" /> Popular
                  </div>
                )}

                <h3 className="text-3xl font-extrabold">{plan.name}</h3>
                <p className="text-gray-500 mt-2 mb-6">{plan.description}</p>

                <div className="mb-8">
                  <div className="flex items-baseline">
                    <span className="text-5xl font-extrabold">${getPrice(plan)}</span>
                    <span className="text-gray-500 ml-2 text-lg">{getInterval(plan)}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-10 !pl-0">
                  {plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <FiCheck className={`w-5 h-5 ${ACCENT_COLOR_TEXT} mr-3`} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={!plan.is_active || currentUserPlan === plan.id}
                  className={`
            w-full py-2 rounded-[4px] font-bold transition-all
            ${ACCENT_COLOR_BG} text-gray-900 shadow-lg
            ${selectedPlan === plan.id ? "ring-2 ring-black scale-[1.01]" : ""}
            ${(!plan.is_active || currentUserPlan === plan.id) ? "opacity-50 cursor-not-allowed" : ""}
          `}
                >
                  {
                    currentUserPlan === plan.id
                      ? "Current Plan"
                      : currentUserPlan && plan.is_free
                        ? "Downgrade to Free"
                        : plan.is_free
                          ? "Start for Free"
                          : "Upgrade to Premium"
                  }
                </button>
              </div>
            ))}
        </div>

        {error && (
          <div className="mt-10 text-center text-red-600 text-sm">
            {error}
          </div>
        )}
      </div>
      <DeleteModal
        show={IsConfirmation}
        onClose={() => setIsConfirmation(false)}
        onConfirm={handleConfirmation}
        title={confirmationTitle}
        confirmText='Confirm'
        confirmButtonStyle='!bg-[#AEF468] text-black !border-none'
        confirmButtonText={confirmationButtonText}
        message={`Are you sure you want to proceed?`}
      />

    </div>
  );
}
